import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { z } from "zod";

// Australian states/territories enum
const AUSTRALIAN_STATES = [
  { value: "NSW", label: "NSW" },
  { value: "VIC", label: "VIC" },
  { value: "QLD", label: "QLD" },
  { value: "SA", label: "SA" },
  { value: "WA", label: "WA" },
  { value: "TAS", label: "TAS" },
  { value: "NT", label: "NT" },
  { value: "ACT", label: "ACT" },
] as const;

const propertyTitleFormSchema = z.object({
  titleReference: z.string().min(1, "Title reference is required"),
  state: z.enum(["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"], {
    required_error: "State is required",
  }),
});

type PropertyTitleFormData = z.infer<typeof propertyTitleFormSchema>;

interface PropertyTitle {
  id: string;
  securityPropertyId: string;
  titleReference: string;
  state: string;
  status: string | null;
  createdAt: string;
}

export interface PropertyTitleFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string;
  existingTitle?: PropertyTitle | null;
}

export function PropertyTitleFormModal({
  open,
  onOpenChange,
  securityId,
  existingTitle,
}: PropertyTitleFormModalProps) {
  const { toast } = useToast();
  const isEditing = !!existingTitle;

  const form = useForm<PropertyTitleFormData>({
    resolver: zodResolver(propertyTitleFormSchema),
    defaultValues: {
      titleReference: "",
      state: undefined,
    },
  });

  // Reset form when existingTitle changes or modal opens
  useEffect(() => {
    if (open) {
      if (existingTitle) {
        form.reset({
          titleReference: existingTitle.titleReference || "",
          state: existingTitle.state as PropertyTitleFormData["state"],
        });
      } else {
        form.reset({
          titleReference: "",
          state: undefined,
        });
      }
    }
  }, [open, existingTitle, form]);

  const saveMutation = useMutation({
    mutationFn: async (data: PropertyTitleFormData) => {
      const endpoint = isEditing
        ? `/api/crm/securities/${securityId}/property/titles/${existingTitle!.id}`
        : `/api/crm/securities/${securityId}/property/titles`;
      const method = isEditing ? "PATCH" : "POST";

      const response = await apiRequest(method, endpoint, data);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to save title");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/securities", securityId, "property", "titles"],
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some(
            (key) =>
              typeof key === "string" &&
              key.includes(`/api/crm/securities/${securityId}`)
          ),
      });
      toast({
        title: "Success",
        description: `Title ${isEditing ? "updated" : "added"} successfully`,
      });
      onOpenChange(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: PropertyTitleFormData) => {
    saveMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        data-testid="property-title-form-modal"
      >
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Title" : "Add Title"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the property title details"
              : "Add a new property title record"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="titleReference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title Reference *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. LOT 42 DP 123456"
                      data-testid="input-title-reference"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value ?? undefined}
                  >
                    <FormControl>
                      <SelectTrigger data-testid="select-state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {AUSTRALIAN_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                data-testid="button-cancel"
                onClick={() => onOpenChange(false)}
                disabled={saveMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="button-save"
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditing ? "Save" : "Add Title"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
